import type { LoyaltyMemberBase } from '~/hooks/apis/reporting/useLoyaltyAnalytics';

import { formatRangeLabel } from '../catalogue/commercial/derive';
import { memberBaseMetrics } from '../catalogue/loyalty/loyaltyBase';
import { RATIO_MEASURES, SOCIETE_TIERS, STOCK_MEASURES, sumKeys } from '../catalogue/loyalty/loyaltyMeasures';
import { loyaltySeries } from '../catalogue/loyalty/loyaltySeries';

import type { AreaStripTile } from './AreaMetricStrip';
import { type SnapshotMetricLike, readAreaMetrics } from './areaMetricReading';
import type { AreaSparkline } from './areaTrend';
import type { SettledMonth } from './settledMonth';

export type LoyaltySplitProgramme = 'luxplus' | 'societe';

/** One tile on the split strip. `source` says which response holds its figure. */
type SplitMetric = {
  id: string;
  label: string;
  /**
   * period: the selected Business Pulse window. settled: the latest settled renewal month, for anything counted on
   * ended membership periods, whose outcomes keep changing for about 17 days. base: the Société member base now.
   */
  source: 'period' | 'base' | 'settled';
  /** Counts that only make sense now (member base) or that are still settling: no "vs last year". */
  noComparison?: string;
  /** Said instead of "Figures unavailable" when the server does not return the metric at all yet. */
  missingNote?: string;
  /**
   * A member base count shown, as a current total, until the server returns this metric with a value: Société Members
   * reads the tier history once le-data governs it, and today's total before that.
   */
  fallbackId?: string;
};

/**
 * A labelled group of More metrics. Its caption says what the tiles count and against what, once for the group, so
 * the tiles themselves carry no window suffix.
 */
type SplitSection = {
  id: string;
  title: string;
  /** stock: counts on the window's last day (members by tier), against the comparison's last day. */
  window: 'period' | 'settled' | 'base' | 'stock';
  metrics: readonly SplitMetric[];
};

type SplitGroup = {
  programme: LoyaltySplitProgramme;
  label: string;
  headline: readonly SplitMetric[];
  sections: readonly SplitSection[];
};

export const NOT_CONNECTED_NOTE = 'Not connected yet';

/**
 * The Loyalty landing split agreed with Ming Chong, 21 Oct 2026: LuxPlus+ and Société side by side, three
 * headline tiles each, the rest behind "More metrics" on the Loyalty page.
 *
 * LuxPlus+ Members and New members come from the LuxPlus+ snapshot (svc-reporting, Business Pulse backend). Until a
 * server returns them the tiles say NOT_CONNECTED_NOTE, never a typed-in figure. Their definitions match the board's
 * LuxPlus+ Executive Overview (Tableau workbook 1940516):
 * - Members: distinct customers with a subscription period active at 00:00 UTC after the window's last day, from
 *   stage.stage_luxplus_active_customers, as the board's "Member Count" (Tableau TODAY() runs in UTC). 87,019 for
 *   20 Oct 2026, the board's figure.
 * - New members: lp_subs_new_members + lp_subs_new_members_promo from analytics.luxplus_executive_summary_regions.
 */
export const LOYALTY_SPLIT: readonly SplitGroup[] = [
  {
    programme: 'luxplus',
    label: 'LuxPlus+',
    headline: [
      { id: 'loyalty.luxplus_members', label: 'Members', source: 'period', missingNote: NOT_CONNECTED_NOTE },
      { id: 'loyalty.luxplus_new_members', label: 'New members', source: 'period', missingNote: NOT_CONNECTED_NOTE },
      { id: 'loyalty.renewal_rate', label: 'Renewal rate', source: 'settled' },
    ],
    sections: [
      {
        id: 'renewals',
        title: 'Renewals',
        window: 'settled',
        metrics: [
          { id: 'loyalty.periods_ended', label: 'Periods ended', source: 'settled' },
          { id: 'loyalty.periods_renewed', label: 'Renewed', source: 'settled' },
          { id: 'loyalty.periods_renewed_then_cancelled', label: 'Renewed then cancelled', source: 'settled' },
          { id: 'loyalty.periods_lapsed', label: 'Lapsed', source: 'settled' },
        ],
      },
      {
        id: 'membership-value',
        title: 'In the membership periods',
        window: 'settled',
        metrics: [
          { id: 'loyalty.luxplus_savings_aud', label: 'Savings given', source: 'settled' },
          { id: 'loyalty.membership_margin_ar', label: 'Margin (AR)', source: 'settled' },
          { id: 'loyalty.membership_orders', label: 'Orders', source: 'settled' },
        ],
      },
      {
        id: 'luxplus-period',
        title: 'This period',
        window: 'period',
        metrics: [
          // Counted by transaction date, so it is final for the selected window.
          { id: 'loyalty.membership_fee_aud', label: 'Membership fees', source: 'period' },
          // This month's open renewals against last year's, which have all resolved since: a change is meaningless.
          {
            id: 'loyalty.periods_awaiting',
            label: 'Awaiting outcome',
            noComparison: 'Still settling',
            source: 'period',
          },
        ],
      },
    ],
  },
  {
    programme: 'societe',
    label: 'Société',
    headline: [
      // Members at the end of the window against the end of the comparison, from the tier change log.
      { id: 'loyalty.societe_members', label: 'Members', source: 'period', fallbackId: 'loyalty.base.members' },
      { id: 'loyalty.points_earned', label: 'Points earned', source: 'period' },
      { id: 'loyalty.points_redeemed', label: 'Points redeemed', source: 'period' },
    ],
    sections: [
      {
        id: 'points',
        title: 'Points',
        window: 'period',
        metrics: [
          { id: 'loyalty.points_burned', label: 'Burned incl. expiry', source: 'period' },
          { id: 'loyalty.redemption_ratio', label: 'Redemption ratio', source: 'period' },
          { id: 'loyalty.burn_ratio', label: 'Burn ratio', source: 'period' },
          { id: 'loyalty.earn_rate', label: 'Earn rate', source: 'period' },
        ],
      },
      {
        id: 'points-sales',
        title: 'Sales with points',
        window: 'period',
        metrics: [
          { id: 'loyalty.points_sales_aud', label: 'Settled with points', source: 'period' },
          { id: 'loyalty.points_sales_gross_aud', label: 'Carrying points', source: 'period' },
        ],
      },
      {
        id: 'members-by-tier',
        title: 'Members by tier',
        window: 'stock',
        metrics: SOCIETE_TIERS.map((tier) => ({
          id: `loyalty.societe_members_${tier.toLowerCase()}`,
          label: tier,
          source: 'period' as const,
        })),
      },
      {
        id: 'enrolments',
        title: 'Enrolments',
        window: 'period',
        metrics: [
          { id: 'loyalty.enrolments', label: 'Enrolments', source: 'period' },
          { id: 'loyalty.enrolments_new_to_le', label: 'New to LE', source: 'period' },
        ],
      },
      {
        id: 'member-base',
        title: 'Member base',
        window: 'base',
        metrics: [
          { id: 'loyalty.base.active', label: 'Active members', source: 'base', noComparison: 'Current total' },
          { id: 'loyalty.base.points', label: 'Points outstanding', source: 'base', noComparison: 'Current total' },
        ],
      },
    ],
  },
];

/** Every headline id in display order: LuxPlus+ first, then Société. */
export const LOYALTY_HEADLINE_IDS: readonly string[] = LOYALTY_SPLIT.flatMap((group) =>
  group.headline.map((metric) => metric.id),
);

const groupMetrics = (group: SplitGroup) => [
  ...group.headline,
  ...group.sections.flatMap((section) => section.metrics),
];

function splitMetric(metricId: string): { programme: LoyaltySplitProgramme; metric: SplitMetric } | undefined {
  for (const group of LOYALTY_SPLIT) {
    const metric = groupMetrics(group).find((candidate) => candidate.id === metricId);
    if (metric) {
      return { metric, programme: group.programme };
    }
  }
  return undefined;
}

/** The programme a split metric belongs to, so opening it lands on that programme's view. Undefined if unknown. */
export function loyaltyProgrammeFor(metricId: string): LoyaltySplitProgramme | undefined {
  return splitMetric(metricId)?.programme;
}

/**
 * Writes the Loyalty page address for a split tile: the overview with Explore open on the tile, in its programme. The
 * page period is left alone: a tile counted on the settled renewal month is explored on that month by the page itself
 * (see exploreLoyaltyState), so the strip keeps showing the period Home showed.
 */
export function applyLoyaltyMetricToParams(params: URLSearchParams, metricId: string) {
  const found = splitMetric(metricId);
  // Home opens the Loyalty page with Explore open on the tile, as it opens Commercial.
  params.set('loyaltyExplore', '1');
  if (found?.programme === 'luxplus') {
    params.set('loyaltyProgram', 'luxplus');
  } else {
    params.delete('loyaltyProgram');
  }
  params.set('loyaltyFocus', metricId);
}

/** The split's own label for a metric, so Explore names it as its tile does. Undefined for a metric not on the split. */
export const loyaltySplitLabel = (metricId: string) => splitMetric(metricId)?.metric.label;

/** True for a Société member base count: a current total with no history, so nothing for Explore to split over time. */
export const isMemberBaseMetric = (metricId: string) => splitMetric(metricId)?.metric.source === 'base';

/** True for a tile that shows a member base total until its own figure arrives (Société Members). */
export const hasMemberBaseFallback = (metricId: string) => Boolean(splitMetric(metricId)?.metric.fallbackId);

/** True when the tile's figure is counted on the settled renewal month rather than the selected period. */
export const isSettledMonthMetric = (metricId: string) => splitMetric(metricId)?.metric.source === 'settled';

type TrendPoint = Parameters<typeof loyaltySeries>[1][number];

export type LoyaltySplitInput = {
  /** Metrics from the LuxPlus+ snapshot for the selected period. */
  luxplus?: readonly SnapshotMetricLike[];
  /** Metrics from the Société snapshot for the selected period. */
  societe?: readonly SnapshotMetricLike[];
  /** Société member base from the Société snapshot. */
  base?: LoyaltyMemberBase | null;
  /** LuxPlus+ metrics for the settled renewal month. The renewal rate carries the server's settled flag. */
  settledLuxplus?: ReadonlyArray<SnapshotMetricLike & { settled?: boolean }> | null;
  settledMonth: SettledMonth;
  /** "Month to date": the window of the More metrics sections counted on the period. */
  periodLabel?: string | null;
  /** The window's last day, which the member counts are read on ("As at 21 Oct 2026"). */
  periodEnd?: string | null;
  /** "vs last year" (the default), "vs previous period", or empty with no comparison: the page's comparison. */
  comparisonPhrase?: string;
  /** Daily points behind each source, for the sparklines. Absent draws none. */
  trends?: {
    luxplus?: ReadonlyArray<TrendPoint>;
    societe?: ReadonlyArray<TrendPoint>;
    settled?: ReadonlyArray<TrendPoint>;
  };
};

export type LoyaltySplitSectionTiles = {
  id: string;
  title: string;
  /** "Sep 2026 vs Sep 2025", "Month to date vs last year" or "Current totals". */
  caption: string;
  tiles: Array<AreaStripTile>;
};

export type LoyaltySplitTiles = Array<{
  programme: LoyaltySplitProgramme;
  label: string;
  /** Labels of the tiles showing canary preview figures, for one note in the card header instead of one per tile. */
  previewLabels: Array<string>;
  headline: Array<AreaStripTile>;
  /** Every More metrics tile in order, for counts and lookups. */
  more: Array<AreaStripTile>;
  sections: Array<LoyaltySplitSectionTiles>;
}>;

/** Daily series of a fortnight or more are smoothed over a week, as Growth's are, so the weekly cycle stays out of a 28px line. */
const SMOOTH_WINDOW = 7;
const SMOOTH_MIN_POINTS = 14;

/**
 * The sparkline for one split tile from its source's daily points. A rate is smoothed as a rolling ratio of sums,
 * never an average of daily rates, so the line agrees with the figure above it. Null when the API sends no daily
 * components for the metric (member counts, the fee, enrolments).
 */
export function loyaltySparkline(metricId: string, trend: ReadonlyArray<TrendPoint> | undefined): AreaSparkline | null {
  if (!trend?.length) {
    return null;
  }
  // A member count is already a level, so it is drawn as it is; smoothing would only lag it.
  const window = trend.length >= SMOOTH_MIN_POINTS && !STOCK_MEASURES[metricId] ? SMOOTH_WINDOW : 1;
  const ratio = RATIO_MEASURES[metricId];
  if (!ratio) {
    const values = loyaltySeries([metricId], trend)[metricId];
    return values?.some((value) => value != null) ? { smoothWindow: window, values } : null;
  }
  const values = trend.map((_, index) => {
    let numerator = 0;
    let denominator = 0;
    for (let at = Math.max(0, index - window + 1); at <= index; at += 1) {
      const point = trend[at];
      if (!point.hasData) {
        continue;
      }
      numerator += sumKeys(point, ratio.numerator) ?? 0;
      denominator += sumKeys(point, ratio.denominator) ?? 0;
    }
    return denominator ? numerator / denominator : null;
  });
  return values.some((value) => value != null) ? { smoothWindow: 1, values } : null;
}

/** A reading becomes a tile the same way areaStripTiles does. */
function toTile(
  metric: SplitMetric,
  found: SnapshotMetricLike | undefined,
  label: string,
  sparkline: AreaSparkline | null = null,
): AreaStripTile {
  const [reading] = readAreaMetrics(
    { metrics: { kind: 'governed', drillBreakdown: null, labels: { [metric.id]: label }, metricIds: [metric.id] } },
    found ? [found] : [],
  );
  if (reading.state !== 'ready') {
    return { id: metric.id, label, note: (!found && metric.missingNote) || 'Figures unavailable', value: null };
  }
  if (metric.noComparison) {
    return {
      changeText: metric.noComparison,
      comparisonLabel: null,
      id: metric.id,
      label,
      sparkline,
      value: reading.value,
    };
  }
  return {
    changeText: reading.changeText,
    favourable: reading.tone === 'good',
    id: metric.id,
    label,
    sparkline,
    value: reading.value,
    // A preview figure (canary only) is named once in its card's header; on the tile it would cut the change line.
    ...(found?.preview ? { preview: true } : {}),
  };
}

function tileFor(
  metric: SplitMetric,
  input: LoyaltySplitInput,
  programme: LoyaltySplitProgramme,
  inMore: boolean,
): AreaStripTile {
  switch (metric.source) {
    case 'base': {
      const found = input.base ? memberBaseMetrics(input.base).find((item) => item.id === metric.id) : undefined;
      return toTile(metric, found, metric.label);
    }
    case 'settled': {
      const month = input.settledMonth;
      // On the headline the month is named next to tiles on the period ("Renewal rate · Sep", the year is in "vs Sep
      // 2025" below, and the full label fits a 152px tile); in More metrics its section names it.
      const label = inMore ? metric.label : `${metric.label} · ${month.label.split(' ')[0]}`;
      const pool = input.settledLuxplus ?? [];
      // The month is chosen to be settled. If the server still says otherwise, the rate reads high because awaiting
      // periods are left out, so no figure is shown rather than a provisional one. Only the renewal rate carries
      // the flag, and it describes the whole window.
      if (pool.find((item) => item.id === 'loyalty.renewal_rate')?.settled === false) {
        return { id: metric.id, label, note: 'Still settling', value: null };
      }
      const tile = toTile(
        metric,
        pool.find((item) => item.id === metric.id),
        label,
        loyaltySparkline(metric.id, input.trends?.settled),
      );
      return tile.value == null ? tile : { ...tile, comparisonLabel: `vs ${month.priorLabel}` };
    }
    default: {
      const pool = programme === 'luxplus' ? input.luxplus : input.societe;
      const found = pool?.find((item) => item.id === metric.id);
      if (metric.fallbackId && found?.value == null && input.base) {
        const fallback: SplitMetric = {
          id: metric.id,
          label: metric.label,
          noComparison: 'Current total',
          source: 'base',
        };
        const base = memberBaseMetrics(input.base).find((item) => item.id === metric.fallbackId);
        return toTile(fallback, base ? { ...base, id: metric.id } : undefined, metric.label);
      }
      return toTile(
        metric,
        found,
        metric.label,
        loyaltySparkline(metric.id, programme === 'luxplus' ? input.trends?.luxplus : input.trends?.societe),
      );
    }
  }
}

/**
 * One metric read as a split tile (value, change and tone), for the line at the top of Explore that shows the figure
 * under the filters chosen there. A metric not on the split reads with its server label.
 */
export function loyaltyMetricTile(metricId: string, found: SnapshotMetricLike | undefined): AreaStripTile {
  const metric: SplitMetric = splitMetric(metricId)?.metric ?? {
    id: metricId,
    label: found?.label ?? metricId,
    source: 'period',
  };
  return toTile(metric, found, metric.label);
}

function sectionCaption(section: SplitSection, input: LoyaltySplitInput): string {
  if (section.window === 'settled') return `${input.settledMonth.label} vs ${input.settledMonth.priorLabel}`;
  if (section.window === 'base') return 'Current totals · active means earned points in the last 12 months';
  if (section.window === 'stock') {
    const day = formatRangeLabel(input.periodEnd, input.periodEnd);
    const comparison = input.comparisonPhrase ?? 'vs last year';
    return `${day ? `As at ${day}` : 'At the end of the period'}${comparison ? ` ${comparison}` : ''}`;
  }
  const comparison = input.comparisonPhrase ?? 'vs last year';
  return `${input.periodLabel ?? 'Selected period'}${comparison ? ` ${comparison}` : ''}`;
}

/** Both halves of the Loyalty split: headline tiles, and More metrics in labelled sections. */
export function loyaltySplitTiles(input: LoyaltySplitInput): LoyaltySplitTiles {
  return LOYALTY_SPLIT.map((group) => {
    const sections = group.sections.map((section) => ({
      caption: sectionCaption(section, input),
      id: section.id,
      tiles: section.metrics.map((metric) => tileFor(metric, input, group.programme, true)),
      title: section.title,
    }));
    const headline = group.headline.map((metric) => tileFor(metric, input, group.programme, false));
    const more = sections.flatMap((section) => section.tiles);
    return {
      headline,
      label: group.label,
      more,
      previewLabels: [...headline, ...more].filter((tile) => tile.preview).map((tile) => tile.label),
      programme: group.programme,
      sections,
    };
  });
}

/** The label of a programme, "LuxPlus+" or "Société". */
export const loyaltyProgrammeLabel = (programme: LoyaltySplitProgramme) =>
  LOYALTY_SPLIT.find((group) => group.programme === programme)?.label ?? programme;
